// Owner approval page for one onboarding ticket. Opening the page reads the
// ticket (safe) but changes nothing. The two state-changing calls — approve and
// reveal — happen only from a deliberate click, once each.
//
// The ticket id alone is not authority: the Worker requires the owner's own
// signed-in session. Nothing here is written to the URL (beyond the id already
// in it), storage or an attribute, and no secret is rendered outside a receipt
// card.

import { AdminApiError, type AdminClient } from "../api.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { clear, h } from "../dom.js";
import type {
  OnboardingApprovalApproveResponse,
  OnboardingApprovalReadResponse,
  OnboardingRevealSecret,
  OnboardingRevealStep,
  StaffRole,
} from "../types.js";
import {
  approvalKindLabel,
  approvalStateLabel,
  capabilitySummary,
  checklistKeyLabel,
  checklistStateLabel,
  companyBasisLabel,
  companyTypeLabel,
  featurePackSummary,
  kindLabel,
  planModeLabel,
  revealStepLabel,
  ticketStepLabel,
} from "../ui/plan_summary.js";
import { button, disclosure, pageHeader } from "../ui/components.js";
import { advice } from "../ui/capabilities.js";
import { dateText } from "../ui/display-words.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { navLabel } from "../ui/sentences.js";
import { runWrite, classifyWriteError } from "../ui/write.js";
import { appendReceipt, renderReceiptCard, renderReceiptFailure } from "../ui/receipt_card.js";

/** The page title is the nav label (`nav-registry.ts`: "Approvals"). */
const PAGE_TITLE = navLabel("approval");

export const REVEAL_EXPLANATION =
  "This shows each key or link the agent prepared, once, and cannot be shown again.";

export const REVEAL_EMPTY_COPY =
  "Nothing is waiting to be shown. Either it was already shown, or the agent has not prepared a key or invitation yet.";

export const REVEAL_MISSING_ID_COPY =
  `No approval was opened. Go to ${navLabel("agent-access")}, enter the ticket id your agent gave you, and open it.`;

export const REVEAL_GENERIC_FAILURE_COPY = "Could not reveal this approval.";

/**
 * A reveal that failed may still have claimed a step: the Worker marks a step
 * used before it mints the secret. So the honest closing line is not "Nothing
 * changed." (rc-2 sister 4).
 */
export const REVEAL_UNCHANGED_COPY =
  "One or more keys or links may have been used. Open the ticket again to see what is left.";

export const REVEAL_CONSUMED_COPY = (stepLabels: readonly string[]): string =>
  `Already revealed on the server and cannot be shown again: ${stepLabels.join(
    " and ",
  )}. Ask your agent for a new request.`;

export const ACTION_CHECK_FAILED_COPY =
  "This page cannot tell whether that finished. Open the ticket again to see its current state.";

/**
 * What an account manager reads where the owner would see Approve and Reveal.
 * The Worker needs the owner's own signed-in session for both (requireHumanOwner),
 * so the account manager is offered neither and told who does it. The words come
 * from the one capability table (item B3b-7), so the owner is never told to ask
 * the owner.
 */
export const AM_APPROVAL_COPY = advice("approve_onboarding_ticket", "account_manager");

/**
 * Decision 1's sentence for an owner whose request was refused. It is built by
 * the one failure home, not typed here (the literal lives only in
 * `ui/failure.ts`).
 */
export const REVEAL_OWNER_REFUSED_COPY = outcomeSentence(
  { kind: "refused", status: 403 },
  "owner",
);

export const APPROVAL_NOT_FOUND_COPY =
  "No approval with that id. Check the id your agent gave you.";

export const APPROVAL_READ_FAILURE_COPY = "Could not read this approval.";

export const APPROVAL_EXPIRED_COPY = (when: string): string =>
  `This approval expired at ${when}. Ask your agent for a new one.`;

export const APPROVAL_STALE_COPY =
  "The plan changed after your agent asked, so this approval no longer matches. Ask your agent for a new one. This page cannot show what changed.";

export const APPROVAL_SUPERSEDED_COPY =
  "A newer request replaced this one. Ask your agent for its latest ticket id.";

export const GO_LIVE_UNAVAILABLE_COPY =
  "Go-live approval is not available yet. The go-live action is built in a later step.";

export const GO_LIVE_STALE_COPY =
  "The checklist changed after your agent asked, so this approval no longer matches.";

/**
 * The confirm sentence names the company, never its web id (rd round 11,
 * R8-01): the id stays in the URL and the ticket, not on the face. The plan's
 * type row above still tells two same-named companies apart.
 */
export const APPROVAL_CONFIRM_COPY = (name: string): string =>
  `Approve this plan for ${name}? Only you can. It lasts 24 hours.`;

export const APPROVAL_CORRECTION_COPY =
  "To change anything, tell your agent: a new request replaces this one.";

export const APPROVAL_REFRESH_FAILED_COPY =
  "Approved, but this page could not refresh. Open the ticket again to see its new state.";

export const APPROVAL_FAILURE_COPY = "Could not approve this plan.";

export const APPROVAL_ALREADY_APPROVED_COPY =
  "This plan was already approved, so nothing more is needed here.";

export const PLAN_UNAVAILABLE_COPY =
  "This page cannot show the plan, so it will not approve it. Ask your agent for a new request.";

export const NOTHING_PREPARED_COPY = "Nothing has been prepared for you to reveal yet.";

export const EVERYTHING_REVEALED_COPY =
  "Everything your agent prepared has already been shown.";

export interface ApprovalRevealPageOptions {
  client: AdminClient;
  root: HTMLElement;
  /** Ticket id from `/approvals/{id}`. */
  approvalId: string | undefined;
  /**
   * Who is looking. Approve and Reveal are the owner's: anything but "owner"
   * (including a caller that forgets) gets the read-only page.
   */
  role: StaffRole;
  isCurrent?: () => boolean;
}

export function revealSecretLabel(
  secret: OnboardingRevealSecret,
  companyName: string,
): string {
  return secret.step === "api_key"
    ? `API key for ${companyName}`
    : `Bank-link invitation for ${companyName}`;
}

/**
 * The company as a person reads it. The plan's display name when there is one;
 * otherwise "this company" (rd-polish-rules). Never the internal web id: it is
 * reachable behind Show details, and a wrong id on the face is worse than a
 * missing name.
 */
function companyFace(ticket: OnboardingApprovalReadResponse): string {
  const named = ticket.plan === null ? "" : ticket.plan.answers.displayName.trim();
  return named === "" ? "this company" : named;
}

const SCOPE_WORDS: Record<string, string> = {
  "reports:read": "read reports",
  "deposits:read": "read deposits",
  "hub:push": "send practice totals",
  "hub_questions:read": "read practice questions",
  "hub_questions:resolve": "answer practice questions",
  "hub_balances:read": "read practice balances",
};

function scopeSentence(scopes: readonly string[]): string {
  const words = scopes.map((scope) => SCOPE_WORDS[scope] ?? "use a listed permission");
  if (words.length === 0) return "This key has no listed permissions.";
  return `This key can ${words.join(" and ")}.`;
}

function formatWhen(iso: string): string {
  const shown = dateText(iso);
  return shown === "" ? "Date not known" : shown;
}

/** What a `service:<name>` identity means to the owner, in words. */
export function requesterLabel(requestedBy: string): string {
  const prefix = "service:";
  if (requestedBy.startsWith(prefix)) {
    const name = requestedBy.slice(prefix.length).trim();
    return name.length === 0 ? "your onboarding agent" : `your onboarding agent (${name})`;
  }
  return requestedBy;
}

/** Fixed sentence: exactly what approving this plan allows. */
export function approvalAllowsCopy(hasApiKey: boolean): string {
  if (hasApiKey) {
    return (
      "Approving this plan lets your onboarding agent register the company if it is not registered already, " +
      "prepare an API key, and prepare a bank-link invitation. The agent never sees the key or the link; you " +
      "open them with Reveal. Approving does not make the company live and does not let the agent do anything else."
    );
  }
  return (
    "Approving this plan lets your onboarding agent register the company if it is not registered already and " +
    "prepare a bank-link invitation. The agent never sees the link; you open it with Reveal. Approving does not " +
    "make the company live and does not let the agent do anything else."
  );
}

/** The server's own sentence when it sent one; never echoed as HTML. */

function labelValue(label: string, value: string): HTMLLIElement {
  return h("li", {}, [h("strong", {}, [`${label}: `]), value]);
}

function listInWords(values: readonly string[], empty: string): string {
  return values.length === 0 ? empty : values.join(", ");
}

function uniqueRevealSteps(
  steps: readonly OnboardingRevealStep[],
): OnboardingRevealStep[] {
  return Array.from(new Set(steps));
}

function preparedSecretSteps(
  ticket: OnboardingApprovalReadResponse,
): OnboardingRevealStep[] {
  const prepared: OnboardingRevealStep[] = [];
  if (ticket.usedSteps.includes("api_key")) prepared.push("api_key");
  if (ticket.usedSteps.includes("link_invitation")) prepared.push("link_invitation");
  return prepared;
}

function planRows(ticket: OnboardingApprovalReadResponse): HTMLLIElement[] {
  const plan = ticket.plan;
  if (plan === null) return [];
  const answers = plan.answers;
  return [
    labelValue("Kind of company", kindLabel(plan.capabilities.kind)),
    labelValue("Name", amVisibleDisplayText(answers.displayName)),
    labelValue("Type", companyTypeLabel(answers.companyType)),
    labelValue("Books basis", companyBasisLabel(answers.basis)),
    labelValue("Books start", answers.booksStart ?? "not set"),
    labelValue("Feature packs", featurePackSummary(answers.featurePacks)),
    labelValue("Hub link", answers.hubLink ?? "none"),
    labelValue("Mode", planModeLabel(plan.mode)),
    labelValue("Adds", capabilitySummary(plan.capabilities)),
  ];
}

function planSection(ticket: OnboardingApprovalReadResponse): HTMLElement {
  const plan = ticket.plan;
  const host = h("section", { class: "card approval-plan", "data-role": "approval-plan" });
  host.append(h("h2", {}, ["What you are approving"]));
  const rows = h("ul", { class: "review-summary" });
  for (const row of planRows(ticket)) rows.append(row);
  host.append(rows);
  host.append(
    h("p", { class: "lede", "data-role": "approval-allows" }, [
      approvalAllowsCopy(plan !== null && plan.capabilities.apiKey),
    ]),
  );
  return host;
}

function checklistSection(ticket: OnboardingApprovalReadResponse): HTMLElement | null {
  if (ticket.checklist === null) return null;
  const host = h("section", {
    class: "card approval-checklist",
    "data-role": "approval-checklist",
  });
  host.append(h("h2", {}, ["Go-live checklist"]));
  const rows = h("ul", { class: "review-summary" });
  for (const row of ticket.checklist) {
    rows.append(
      labelValue(checklistKeyLabel(row.key), checklistStateLabel(row.state)),
    );
  }
  host.append(rows);
  return host;
}

function factsBlock(ticket: OnboardingApprovalReadResponse): HTMLElement[] {
  const used = ticket.usedSteps.map((step) => ticketStepLabel(step));
  const revealed = uniqueRevealSteps(ticket.revealedSteps).map((step) =>
    revealStepLabel(step),
  );

  const nodes: HTMLElement[] = [
    h("p", { "data-role": "approval-company" }, [
      h("strong", {}, ["Company: "]),
      amVisibleDisplayText(companyFace(ticket)),
      // The web id is the same kind of internal key as the ticket id: it stays
      // behind Show details so the owner can still tell two companies apart
      // without reading a slug on the face (R8-01).
      disclosure("Show details", h("p", {}, [`Web id: ${ticket.company}`])),
    ]),
    labelValue("Kind", approvalKindLabel(ticket.kind)),
    h("p", { "data-role": "approval-state" }, [
      h("strong", {}, ["State: "]),
      approvalStateLabel(ticket.state),
    ]),
    h("p", { "data-role": "approval-requested-by" }, [
      h("strong", {}, ["Requested by: "]),
      amVisibleDisplayText(requesterLabel(ticket.requestedBy)),
    ]),
  ];
  if (ticket.approvedBy !== null) {
    nodes.push(
      h("p", { "data-role": "approval-approved-by" }, [
        h("strong", {}, ["Approved by: "]),
        amVisibleDisplayText(ticket.approvedBy),
      ]),
    );
  }
  if (ticket.expiresAt !== null) {
    nodes.push(
      h("p", { "data-role": "approval-expires" }, [
        h("strong", {}, ["Expires: "]),
        formatWhen(ticket.expiresAt),
      ]),
    );
  }
  nodes.push(
    h("p", { "data-role": "approval-used-steps" }, [
      h("strong", {}, ["Steps run: "]),
      listInWords(used, "none"),
    ]),
    h("p", { "data-role": "approval-revealed-steps" }, [
      h("strong", {}, ["Steps revealed: "]),
      listInWords(revealed, "none"),
    ]),
  );
  if (ticket.plan !== null) {
    nodes.push(planSection(ticket));
  }
  const checklist = checklistSection(ticket);
  if (checklist !== null) nodes.push(checklist);
  return nodes;
}

function stateSentence(ticket: OnboardingApprovalReadResponse): string | null {
  if (ticket.kind === "plan") {
    if (ticket.state === "expired") {
      return APPROVAL_EXPIRED_COPY(
        ticket.expiresAt === null ? "an unrecorded time" : formatWhen(ticket.expiresAt),
      );
    }
    if (ticket.state === "stale") return APPROVAL_STALE_COPY;
    if (ticket.state === "superseded") return APPROVAL_SUPERSEDED_COPY;
    if (ticket.state === "pending" && ticket.plan === null) return PLAN_UNAVAILABLE_COPY;
    return null;
  }

  // Go-live first: no go-live action exists, so approving would authorise
  // nothing. State-specific wording still tells the truth about the ticket.
  if (ticket.state === "stale") return GO_LIVE_STALE_COPY;
  if (ticket.state === "expired") {
    return APPROVAL_EXPIRED_COPY(
      ticket.expiresAt === null ? "an unrecorded time" : formatWhen(ticket.expiresAt),
    );
  }
  if (ticket.state === "superseded") return APPROVAL_SUPERSEDED_COPY;
  return GO_LIVE_UNAVAILABLE_COPY;
}

function canApprove(ticket: OnboardingApprovalReadResponse): boolean {
  return ticket.kind === "plan" && ticket.state === "pending" && ticket.plan !== null;
}

/**
 * Owner approval page. The first read is safe; approve and reveal each happen
 * only from their own click. A re-read repaints only the facts block, so an
 * action receipt is never rebuilt or lost.
 */
export async function renderApprovalRevealPage(
  opts: ApprovalRevealPageOptions,
): Promise<void> {
  const { client, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const ownerMayAct = opts.role === "owner";
  const subtitle = ownerMayAct
    ? "Read the request, then approve it or show the prepared key."
    : "Read the request.";
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);

  page.append(pageHeader(PAGE_TITLE, subtitle));

  const id = opts.approvalId?.trim() ?? "";
  if (id.length === 0) {
    page.append(
      h("p", { class: "empty-state", role: "status", "data-role": "approval-missing" }, [
        // The account manager cannot open Agent access, so that sentence would
        // send them to a page that refuses them.
        ownerMayAct ? REVEAL_MISSING_ID_COPY : AM_APPROVAL_COPY,
      ]),
    );
    return;
  }

  const ticketLine = h("p", { "data-role": "approval-ticket-id" }, [
    "This is one approval.",
    disclosure("Show details", h("code", {}, [id])),
  ]);
  page.append(ticketLine);
  const loading = h("p", { class: "muted", role: "status", "data-role": "approval-loading" }, [
    "Loading approval…",
  ]);
  page.append(loading);

  let ticket: OnboardingApprovalReadResponse;
  try {
    ticket = await client.getApproval(id);
  } catch (error) {
    if (!stillCurrent()) return;
    clear(page);
    const readError = h("p", { class: "banner banner-err", role: "alert", "data-role": "approval-read-error" }, [
      error instanceof AdminApiError && error.status === 404
        ? APPROVAL_NOT_FOUND_COPY
        : APPROVAL_READ_FAILURE_COPY,
    ]);
    page.append(pageHeader(PAGE_TITLE), ticketLine, readError);
    if (!(error instanceof AdminApiError) || error.status !== 404) {
      const retry = button("Try again", "primary", { "data-action": "retry", "aria-label": "Retry" });
      retry.addEventListener("click", () => {
        void renderApprovalRevealPage(opts);
      });
      page.append(retry);
    }
    return;
  }
  if (!stillCurrent()) return;

  clear(page);
  const factsHost = h("div", { class: "card approval-facts", "data-role": "approval-facts" });
  const actionHost = h("div", { class: "card approval-action", "data-role": "approval-action" });
  const revealHost = h("div", {
    class: "card approval-reveal",
    "data-role": "approval-reveal-section",
  });
  page.append(
    pageHeader(PAGE_TITLE, subtitle),
    ticketLine,
    factsHost,
    actionHost,
    ...(ownerMayAct ? [revealHost] : []),
  );

  const paintFacts = (next: OnboardingApprovalReadResponse): void => {
    clear(factsHost);
    factsHost.append(...factsBlock(next));
  };

  paintFacts(ticket);

  // The account manager reads the request and stops here: Approve and Reveal
  // are the owner's, and the Worker refuses both for anyone else.
  if (!ownerMayAct) {
    actionHost.append(h("p", { "data-role": "approval-owner-only" }, [AM_APPROVAL_COPY]));
    return;
  }

  // ---- Approve -----------------------------------------------------------
  if (canApprove(ticket)) {
    // The confirm names the company the way the facts block does: display name,
    // never the web id (R8-01).
    const name = amVisibleDisplayText(companyFace(ticket));
    const approve = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "approve-approval",
      },
      ["Approve this plan"],
    );
    const confirmHost = h("div", {
      "data-role": "approve-confirm",
      hidden: "true",
    });
    const receiptHost = h("div", { "data-role": "approval-action-receipt" });
    let confirmOpen = false;
    let inFlight = false;

    approve.addEventListener("click", () => {
      if (confirmOpen || inFlight) return;
      confirmOpen = true;
      clear(confirmHost);
      confirmHost.hidden = false;
      confirmHost.removeAttribute("hidden");
      const yes = h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "confirm-approve-approval",
        },
        ["Yes, approve"],
      );
      const notYet = h(
        "button",
        {
          type: "button",
          class: "btn btn-ghost",
          "data-action": "cancel-approve-approval",
        },
        ["Not yet"],
      );
      // Deliberately no focus() and no pre-selected state: the owner must
      // choose, not accept a default action.
      confirmHost.append(
        h("p", { "data-role": "approve-confirm-copy" }, [
          APPROVAL_CONFIRM_COPY(name),
        ]),
        h("div", { class: "form-row" }, [yes, notYet]),
        h("p", { class: "muted", "data-role": "approve-correction" }, [
          APPROVAL_CORRECTION_COPY,
        ]),
      );
      notYet.addEventListener("click", () => {
        if (inFlight) return;
        confirmOpen = false;
        clear(confirmHost);
        confirmHost.hidden = true;
        confirmHost.setAttribute("hidden", "true");
      });
      yes.addEventListener("click", () => {
        void (async () => {
          if (inFlight) return;
          inFlight = true;
          const held: { result: OnboardingApprovalApproveResponse | null } = { result: null };
          const outcome = await runWrite({
            key: `approve-approval:${id}`,
            scope: actionHost,
            gate: () => null,
            send: () => client.approveApproval(id),
            afterLanded: (value) => {
              held.result = value;
            },
            stillCurrent,
          });
          if (outcome.kind === "landed") {
            if (!stillCurrent() || held.result === null) return;
            const approved: OnboardingApprovalApproveResponse = held.result;
            // One press, one approval: the confirm's own button stays disabled
            // after the write landed.
            yes.disabled = true;
            approve.disabled = true;
            // Keep the action block: collapse the confirm and leave the
            // receipt where it is. Removing the now-actionless button makes
            // the page truthful without rebuilding the receipt host.
            clear(confirmHost);
            confirmHost.hidden = true;
            confirmHost.setAttribute("hidden", "true");
            approve.remove();
            clear(receiptHost);
            appendReceipt(
              receiptHost,
              renderReceiptCard({
                happened: `Approved ${amVisibleDisplayText(companyFace(ticket))}, expires ${formatWhen(approved.expiresAt)}.`,
                unchanged:
                  "Nothing has run yet, and the agent cannot go live.",
                next: {
                  who: "Your onboarding agent",
                  text: "Register the company, then you open the key or link here.",
                },
              }),
            );

            // One re-read repaints facts only; the receipt must survive it.
            try {
              const refreshed = await client.getApproval(id);
              if (!stillCurrent()) return;
              paintFacts(refreshed);
            } catch {
              if (!stillCurrent()) return;
              factsHost.append(
                h(
                  "p",
                  {
                    class: "banner banner-err",
                    role: "alert",
                    "data-role": "approval-refresh-failed",
                  },
                  [APPROVAL_REFRESH_FAILED_COPY],
                ),
              );
            }
            return;
          }
          if (!stillCurrent()) return;
          // A failure does not prove the ticket is unchanged: the server may
          // have approved it, or moved it on, before the call failed. The
          // receipt says what is known, never "Nothing changed." (rc-2
          // sister 4), and the page re-reads before offering a retry. Round 3
          // reverted approve to this round-1 unconfirmed wording: approve has a
          // re-read, so it does not carry the credential-issue possibly-sent
          // line (that line is for reveal and the four issue paths).
          approve.disabled = true;
          yes.disabled = true;
          const copy = failureCopy(outcome.error, { role: opts.role, context: "write" });
          clear(receiptHost);
          const receipt = renderReceiptFailure(
            copy.sentence,
            outcomeSentence(outcome, opts.role),
          );
          if (copy.detail !== null) {
            receipt.append(disclosure("Show details", h("p", {}, [copy.detail])));
          }
          appendReceipt(receiptHost, receipt);

          {
            let refreshed: OnboardingApprovalReadResponse;
            try {
              refreshed = await client.getApproval(id);
            } catch {
              if (!stillCurrent()) return;
              // Fail closed: the page cannot say what the server now holds, so
              // the action stays unusable and the confirm collapses to the same
              // hidden host the other terminal paths use.
              clear(confirmHost);
              confirmHost.hidden = true;
              confirmHost.setAttribute("hidden", "true");
              actionHost.append(
                h(
                  "p",
                  {
                    class: "banner banner-err",
                    role: "alert",
                    "data-role": "approval-action-check-failed",
                  },
                  [ACTION_CHECK_FAILED_COPY],
                ),
              );
              return;
            }
            if (!stillCurrent()) return;
            paintFacts(refreshed);

            if (refreshed.state === "approved") {
              // `stateSentence` is null for an approved plan, so this needs its
              // own sentence beside the receipt.
              clear(confirmHost);
              confirmHost.hidden = true;
              confirmHost.setAttribute("hidden", "true");
              approve.remove();
              actionHost.append(
                h("p", { class: "muted", "data-role": "approval-already-approved" }, [
                  APPROVAL_ALREADY_APPROVED_COPY,
                ]),
              );
              return;
            }

            if (!canApprove(refreshed)) {
              clear(confirmHost);
              confirmHost.hidden = true;
              confirmHost.setAttribute("hidden", "true");
              approve.remove();
              const sentence = stateSentence(refreshed);
              if (sentence !== null) {
                actionHost.append(
                  h("p", { "data-role": "approval-state-note" }, [sentence]),
                );
              }
              return;
            }

            // Still approvable: retry stays available, exactly as before, but
            // only after the re-read has settled.
            yes.disabled = false;
            approve.disabled = false;
            inFlight = false;
          }
        })();
      });
    });

    actionHost.append(
      approve,
      h("p", { class: "muted", "data-role": "approve-correction-source" }, [
        APPROVAL_CORRECTION_COPY,
      ]),
      confirmHost,
      receiptHost,
    );
  } else {
    const sentence = stateSentence(ticket);
    if (sentence !== null) {
      actionHost.append(
        h("p", { "data-role": "approval-state-note" }, [sentence]),
      );
    }
  }

  // ---- Reveal ------------------------------------------------------------
  const prepared = preparedSecretSteps(ticket);
  const unrevealed = prepared.filter(
    (step) => !uniqueRevealSteps(ticket.revealedSteps).includes(step),
  );
  revealHost.append(h("h2", {}, ["Reveal"]));
  if (unrevealed.length === 0) {
    revealHost.append(
      h(
        "p",
        { class: "empty", role: "status", "data-role": "approval-reveal-empty" },
        [prepared.length === 0 ? NOTHING_PREPARED_COPY : EVERYTHING_REVEALED_COPY],
      ),
    );
    return;
  }

  const reveal = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "reveal-approval",
    },
    ["Reveal now"],
  );
  const resultHost = h("div", { "data-role": "approval-reveal-result" });
  // The consumed notice lives outside resultHost: a later success clears only
  // the result, and the loss must stay visible for the life of the page.
  const consumedHost = h("div", { "data-role": "approval-reveal-consumed" });
  const notifiedLost: OnboardingRevealStep[] = [];
  let consumedNotice: HTMLElement | null = null;
  const showConsumed = (steps: readonly OnboardingRevealStep[]): void => {
    let changed = false;
    for (const step of steps) {
      if (!notifiedLost.includes(step)) {
        notifiedLost.push(step);
        changed = true;
      }
    }
    if (!changed) return;
    const copy = REVEAL_CONSUMED_COPY(
      notifiedLost.map((step) => revealStepLabel(step)),
    );
    if (consumedNotice === null) {
      consumedNotice = h("p", { "data-role": "reveal-consumed" }, [copy]);
      consumedHost.append(consumedNotice);
      return;
    }
    consumedNotice.textContent = copy;
  };
  const readyLine = h("p", { "data-role": "approval-ready-to-reveal" }, [
    `Ready to reveal: ${unrevealed.map((step) => revealStepLabel(step)).join(", ")}.`,
  ]);
  const showReadyToReveal = (steps: readonly OnboardingRevealStep[]): void => {
    if (steps.length === 0) {
      readyLine.remove();
      return;
    }
    readyLine.textContent = `Ready to reveal: ${steps
      .map((step) => revealStepLabel(step))
      .join(", ")}.`;
  };
  revealHost.append(
    h("p", { class: "lede", "data-role": "approval-reveal-explanation" }, [
      REVEAL_EXPLANATION,
    ]),
    readyLine,
    reveal,
    resultHost,
    consumedHost,
  );

  reveal.addEventListener("click", () => {
    // One render, one reveal. A double click must not mint twice.
    if (reveal.disabled) return;
    reveal.disabled = true;
    reveal.setAttribute("disabled", "true");
    void (async () => {
      try {
        const response = await client.revealApproval(id);
        if (!stillCurrent()) return;
        clear(resultHost);
        if (response.secrets.length === 0) {
          resultHost.append(
            h("p", { class: "empty", role: "status", "data-role": "reveal-empty" }, [
              REVEAL_EMPTY_COPY,
            ]),
          );
        } else {
          for (const [index, secret] of response.secrets.entries()) {
            const card = renderReceiptCard(
              {
                happened: revealSecretLabel(secret, companyFace(ticket)),
                unchanged: `Expires ${formatWhen(secret.expiresAt)}.`,
                next: null,
              },
              {
                oneTime: { label: "Copy it now", value: secret.token },
                oneTimeRole: `reveal-secret-${String(index)}`,
              },
            );
            if (
              secret.step === "api_key" &&
              secret.scopes !== undefined &&
              secret.scopes.length > 0
            ) {
              card.append(
                h("div", { class: "muted", "data-role": "reveal-scopes" }, [
                  h("p", {}, [scopeSentence(secret.scopes)]),
                  disclosure(
                    "Show details",
                    h("p", {}, [`Scopes: ${secret.scopes.join(", ")}`]),
                  ),
                ]),
              );
            }
            appendReceipt(resultHost, card);
          }
        }

        // R8-03: the line said what was still waiting; once the secrets are on
        // screen it is no longer true. The receipt is the truth; the ready line
        // goes. A failed re-read must not put it back.
        const revealedNow = new Set(response.secrets.map((secret) => secret.step));
        showReadyToReveal(
          response.secrets.length === 0
            ? []
            : unrevealed.filter((step) => !revealedNow.has(step)),
        );

        // Facts only: the receipt above must stay, and "revealed" must not be
        // stale beside a secret that is now on screen.
        try {
          const refreshed = await client.getApproval(id);
          if (!stillCurrent()) return;
          paintFacts(refreshed);
          // The fresh read is the better truth: list exactly what is still
          // waiting, or nothing when the ticket says every step was shown.
          const stillHidden = prepared.filter(
            (step) => !uniqueRevealSteps(refreshed.revealedSteps).includes(step),
          );
          showReadyToReveal(stillHidden);
        } catch {
          // The receipt is the truth of what happened; no extra noise.
        }
      } catch (error) {
        if (!stillCurrent()) return;
        clear(resultHost);
        if (error instanceof AdminApiError && error.status === 403) {
          resultHost.append(
            h(
              "p",
              {
                class: "empty",
                role: "status",
                "data-role": "reveal-owner-refused",
              },
              [REVEAL_OWNER_REFUSED_COPY],
            ),
          );
          return;
        }
        // Never guess another cause (404/network/5xx): say only that it failed,
        // and that a step may already have been claimed on the server. An
        // unknown answer reads the possibly-sent sentence, never the shared
        // "refresh" wording (round 2 M1: the Worker claims the step before it
        // mints, so a reload is the one path that can lose the secret).
        appendReceipt(
          resultHost,
          renderReceiptFailure(
            classifyWriteError(error).kind === "unknown"
              ? outcomeSentence({ kind: "unknown", blocked: true }, opts.role)
              : REVEAL_GENERIC_FAILURE_COPY,
            REVEAL_UNCHANGED_COPY,
          ),
        );

        // The Worker claims a step before it mints it, so a failed call may
        // have consumed one or more steps. Read the ticket once and compute what
        // is lost against what was unrevealed when this page rendered.
        let refreshed: OnboardingApprovalReadResponse;
        try {
          refreshed = await client.getApproval(id);
        } catch {
          if (!stillCurrent()) return;
          // Fail closed: Reveal stays disabled because the page cannot say
          // whether the Worker consumed anything.
          resultHost.append(
            h(
              "p",
              {
                class: "banner banner-err",
                role: "alert",
                "data-role": "approval-action-check-failed",
              },
              [ACTION_CHECK_FAILED_COPY],
            ),
          );
          return;
        }
        if (!stillCurrent()) return;
        paintFacts(refreshed);

        const nowRevealed = uniqueRevealSteps(refreshed.revealedSteps);
        const lost = unrevealed.filter((step) => nowRevealed.includes(step));
        const remaining = unrevealed.filter((step) => !nowRevealed.includes(step));
        if (lost.length > 0) {
          showReadyToReveal(remaining);
          showConsumed(lost);
        }

        // Re-enable only for an HTTP answer from the Worker with a step left
        // unrevealed. A transport failure may still be running on the Worker,
        // so a second click could race it and lose the secret.
        if (error instanceof AdminApiError && remaining.length > 0) {
          reveal.disabled = false;
          reveal.removeAttribute("disabled");
          return;
        }
        if (!(error instanceof AdminApiError)) {
          resultHost.append(
            h(
              "p",
              {
                class: "banner banner-err",
                role: "alert",
                "data-role": "approval-action-check-failed",
              },
              [ACTION_CHECK_FAILED_COPY],
            ),
          );
        }
      }
    })();
  });
}
